import { useState, useRef, useEffect } from "react";
import Modal from "../../../../components/Modal";
import { downloadAgreementTemplate } from "../../../../actions/CarrierAgreementMgmt/Agreements";
import { MassUploadTable } from "./MassUploadTable";
import ShowButton from "../../../../components/ShowButton";
import UploadFileButton from "../../../../components/UploadFileButton";
import { ArrowDownCircleIcon } from "@heroicons/react/24/solid";
import { toast } from "../../../../components/toastNotification";
import Tooltip from "react-tooltip";
import EditButton from "../../../../components/EditButton";
import { useRecoilState, useRecoilValue } from "recoil";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import {
  agmtAllMassUploadData,
  agmtEditMassUpload,
  agmtMassUploadData,
  agmtRowData,
  agreementForEdit,
} from "../atoms/atoms";
import useUploadMassUploadFile from "../hooks/useUploadMassUploadFile";
import AgreementForm from "../forms/AgreementForm";
import AgmtDetails from "../views/AgmtDetails";
import CloseIconButton from "../../../../components/CloseIconButton";

export const MassUpload = ({
  isOpen,
  setMassUploadModal,
  isFranchiseUpload,
  agmtRefetch,
}) => {
  const [allMassUploadData, setAllMassUpload] = useRecoilState(
    agmtAllMassUploadData
  );
  const [massUploadData, setMassUploadData] =
    useRecoilState(agmtMassUploadData);
  const [isEditMassUpload, setEditMassUpload] =
    useRecoilState(agmtEditMassUpload);
  const [, setRowToEdit] = useRecoilState(agreementForEdit);
  const [agmtDetails, setAgmtDetails] = useRecoilState(agmtRowData);
  const isEditState = useRecoilValue(agmtEditMassUpload);
  const [isShowState, setShow] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [toggleClear, setToggleCleared] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { mutateAsync: uploadAgreement, isLoading } = useUploadMassUploadFile();

  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toISOString().split("T")[0] : null;

  const formatFloat = (float) => parseFloat(float || 0).toFixed(2);

  const inputRef = useRef();

  const handleShow = (row) => {
    setShow(true);
    setAgmtDetails({
      ...row,
      effectiveDate: formatDate(row.effectiveDate),
      expirationDate: formatDate(row.expirationDate),
      commissionEffectiveDate: formatDate(row.commissionEffectiveDate),
      newPercentage: formatFloat(row.newPercentage),
      renewalPercentage: formatFloat(row.renewalPercentage),
      newPercentageConfie: formatFloat(row.newPercentageConfie),
      newPercentageFranchise: formatFloat(row.newPercentageFranchise),
      renewPercentageConfie: formatFloat(row.renewPercentageConfie),
      renewPercentageFranchise: formatFloat(row.renewPercentageFranchise),
    });
    setEditMassUpload(false);
  };
  const handleEdit = (rowData) => {
    const row = isShowState ? agmtDetails : rowData;
    const states = row.state.split(",");
    const policies = row.policyType.split(",");
    const defaultPolicy = policies.map((policy) => {
      return { label: policy, value: policy };
    });
    const defaultStates = states.map((state) => {
      return { label: state, value: state };
    });

    setRowToEdit({
      ...row,
      effectiveDate: formatDate(row.effectiveDate),
      expirationDate: formatDate(row.expirationDate),
      commissionEffectiveDate: formatDate(row.commissionEffectiveDate),
      state: defaultStates,
      policyType: defaultPolicy,
      newPercentage: formatFloat(row.newPercentage),
      renewalPercentage: formatFloat(row.renewalPercentage),
      newPercentageConfie: formatFloat(row.newPercentageConfie),
      newPercentageFranchise: formatFloat(row.newPercentageFranchise),
      renewPercentageConfie: formatFloat(row.renewPercentageConfie),
      renewPercentageFranchise: formatFloat(row.renewPercentageFranchise),
    });
    setEditMassUpload(true);
  };

  const columns = [
    {
      name: "Findings",
      cell: (row) => {
        return <div>{row.condition}</div>;
      },
    },
    {
      name: "Carrier",
      center: true,
      cell: (row) => {
        return <div className="text-center">{row.carrier}</div>;
      },
    },
    {
      name: "New %",
      selector: (row) => {
        return (
          <div className="text-center">{formatFloat(row.newPercentage)}</div>
        );
      },
    },
    {
      name: "New Confie %",
      selector: (row) => {
        return (
          <div className="text-center">
            {formatFloat(row.newPercentageConfie)}
          </div>
        );
      },
      omit: !isFranchiseUpload,
    },
    {
      name: "New Franchise %",
      selector: (row) => {
        return (
          <div className="text-center">
            {formatFloat(row.newPercentageFranchise)}
          </div>
        );
      },
      omit: !isFranchiseUpload,
    },
    {
      name: "Renewal %",
      selector: (row) => {
        return (
          <div className="text-center">
            {formatFloat(row.renewalPercentage)}
          </div>
        );
      },
    },
    {
      name: "Renewal Confie %",
      selector: (row) => {
        return (
          <div className="text-center">
            {formatFloat(row.renewPercentageConfie)}
          </div>
        );
      },
      omit: !isFranchiseUpload,
    },
    {
      name: "Renewal Franchise %",
      selector: (row) => {
        return (
          <div className="text-center">
            {formatFloat(row.renewPercentageFranchise)}
          </div>
        );
      },
      omit: !isFranchiseUpload,
    },
    {
      name: "State",
      omit: isFranchiseUpload,
      center: true,
      cell: (row) => {
        return <div className="text-center">{row.state}</div>;
      },
    },
    {
      name: "Agency",
      center: true,
      cell: (row) => {
        return <div className="text-center">{row.agency}</div>;
      },
      omit: isFranchiseUpload,
    },
    {
      name: "Entity",
      center: true,

      cell: (row) => {
        return <div className="text-center">{row.entityName}</div>;
      },
      omit: isFranchiseUpload,
    },
    {
      name: "Master Legal Entity",
      center: true,

      cell: (row) => {
        return <div className="text-center">{row.masterEntityName}</div>;
      },
      omit: isFranchiseUpload,
    },
    {
      name: "Effective Date",
      selector: (row) => {
        return (
          <div className="text-center">
            {row.effectiveDate === null
              ? ""
              : new Date(row.effectiveDate).toLocaleDateString()}
          </div>
        );
      },
    },
    {
      name: "Notes",
      center: true,
      cell: (row) => {
        return (
          <div className="text-center">
            {row.notes && row.notes.length > 30 ? (
              <span data-tip={row.notes}>
                <Tooltip
                  place="top"
                  effect="solid"
                  type="info"
                  border={true}
                  arrowColor="#0078d7"
                  borderColor="#0078d7"
                  backgroundColor="white"
                  textColor="#0078d7"
                  className="max-w-xs"
                />
                {row.notes.substring(0, 30).trim() + "..."}
              </span>
            ) : (
              row.notes
            )}
          </div>
        );
      },
    },
    {
      name: "Actions",
      center: "true",
      width: "13%",
      cell: (row) => {
        return (
          <div className=" flex flex-row justify-center items-center opacity-100 space-x-1">
            <div className="flex flex-row">
              <div className="flex flex-row flex-col justify-center items-center opacity-100 gap-x-2 space-x-1 space-y-1">
                <ShowButton
                  className={
                    (isShowState || isEditMassUpload || selectedRows?.length > 0
                      ? "opacity-50"
                      : "opacity-100") + ""
                  }
                  disabled={
                    isEditMassUpload || isShowState || selectedRows?.length > 0
                  }
                  buttonText="Show"
                  onClick={() => {
                    handleShow(row, "Show");
                  }}
                />
                <EditButton
                  className={
                    (isShowState || selectedRows?.length > 0
                      ? "opacity-50"
                      : "opacity-100") + ""
                  }
                  disabled={
                    isEditMassUpload || isShowState || selectedRows?.length > 0
                  }
                  buttonText="Edit"
                  onClick={() => {
                    handleEdit(row);
                  }}
                />
              </div>
            </div>
          </div>
        );
      },
    },
  ];
  const attachFile = (e) => {
    handleReset();
    const forUpload = new FormData();
    forUpload.append("file", e.target.files[0]);
    uploadFile(forUpload);
    e.target.value = null;
  };

  const uploadFile = (forUpload) => {
    uploadAgreement([forUpload, isFranchiseUpload], {
      onSuccess: (res) => {
        if (res) {
          if (res.status === 200) {
            if (res.data?.length > 0) {
              const uploadedData = res.data.map((d) => {
                return {
                  ...d,
                  expandRow: false,
                  newPercentage: formatFloat(d.newPercentage),
                  newPercentageConfie: formatFloat(d.newPercentageConfie),
                  newPercentageFranchise: formatFloat(d.newPercentageFranchise),
                  renewalPercentage: formatFloat(d.renewalPercentage),
                  renewPercentageConfie: formatFloat(d.renewPercentageConfie),
                  renewPercentageFranchise: formatFloat(
                    d.renewPercentageFranchise
                  ),
                };
              });
              setMassUploadData(uploadedData);
              setAllMassUpload(uploadedData);
            } else {
              toast.fire(
                "Uploading Agreements",
                "File has no data to read",
                "warning"
              );
              setMassUploadData([]);
              setAllMassUpload([]);
            }
          } else {
            if (res.data) {
              const errorMessage = res.data.message
                ? res.data.message
                : res.data;
              toast.fire("Uploading Agreements Failed", errorMessage, "error");
              setMassUploadData([]);
              setAllMassUpload([]);
            }
          }
        }
      },
      onError: (err) => {
        toast.fire("Uploading Agreement Failed", err, "error");
        setMassUploadData([]);
        setAllMassUpload([]);
      },
    });
    if (inputRef.current) {
      inputRef.current.value = null;
    }
    updatedDataHandler("All", 0);
  };
  const filters = ["All", "Valid", "Invalid"];
  const updatedDataHandler = (filter, index = 0) => {
    let filtered = allMassUploadData;

    if (filter === "Valid") {
      filtered = allMassUploadData.filter(
        (d) => d.condition?.toLowerCase() === "valid"
      );
    } else if (filter === "Invalid") {
      filtered = allMassUploadData.filter(
        (d) => d.condition?.toLowerCase() !== "valid"
      );
    }

    setMassUploadData(filtered);
    setActiveTab(index);
  };

  const DownloadTemplate = () => {
    downloadAgreementTemplate(isFranchiseUpload).then((content) => {
      if (content.status === 200) {
        var fileName = "";
        if (isFranchiseUpload) {
          fileName = "MassUploadAgreementsTemplate_Franchise.xlsx";
        } else {
          fileName = "MassUploadAgreementsTemplate.xlsx";
        }

        var url = window.URL.createObjectURL(content.data);

        let tempLink = document.createElement("a");
        tempLink.href = url;
        tempLink.setAttribute("download", fileName);
        tempLink.click();

        toast.fire("Success", "File Downloaded Successfully", "success");
      }
      if (content.status === 404) {
        toast.fire("Error Downloading File:", "File not found", "error");
      }
    });
  };

  const handleSelectedRowsChange = (selected) => {
    setSelectedRows(selected.selectedRows);
  };

  const handleDelete = () => {
    const ids = selectedRows.map((d) => d.id);

    const updatedAll = allMassUploadData.filter((d) => !ids.includes(d.id));
    setAllMassUpload(updatedAll);

    const currentFilter = filters[activeTab]; // All, Valid, or invalid
    let updatedFiltered = [];

    if (currentFilter === "All") {
      updatedFiltered = updatedAll;
    } else if (currentFilter === "Valid") {
      updatedFiltered = updatedAll.filter(
        (d) => d.condition?.toLowerCase?.() === "valid"
      );
    } else if (currentFilter === "Invalid") {
      updatedFiltered = updatedAll.filter(
        (d) => d.condition?.toLowerCase?.() !== "valid"
      );
    }

    setMassUploadData(updatedFiltered);

    setToggleCleared(!toggleClear);
    setShow(false);
    setOpenModal(false);
    setSelectedRows([]);
    toast.fire("Delete Agreement", "Agreement succesfully removed", "success");
  };

  const handleReset = () => {
    setMassUploadData([]);
    setAllMassUpload([]);
    setEditMassUpload(false);
    setSelectedRows([]);
    setShow(false);
  };

  //Refresh the table when data changes
  useEffect(() => {
    if (!isEditState && allMassUploadData.length > 0) {
      const currentFilter = filters[activeTab];
      updatedDataHandler(currentFilter, activeTab);
    }
  }, [isEditState, allMassUploadData]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        handleReset();
        setMassUploadModal(false);
      }}
      closeOutside={false}
      hasCloseButton={true}
      width="full"
    >
      <>
        <>
          <div className="font-bold text-xl flex items-center border-b-4 border-secondary py-4">
            <div className="text-base text-primary font-bold">
              Agreement Mass Upload
            </div>
            {!isShowState && !isEditState && (
              <div className="ml-auto flex items-center">
                <button
                  onClick={DownloadTemplate}
                  className="hover:scale-90 transition duration-500 bg-tranparent mr-2 border-secondary shadow-md hover:bg-secondary text-secondary group relative flex justify-center items-center py-1 px-3 border text-xs font-medium rounded-full hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary focus:ring-opacity-70"
                >
                  <ArrowDownCircleIcon className="h-4 w-4 mr-2" />
                  Template
                </button>
                <UploadFileButton onChange={attachFile} disabled={false} />
              </div>
            )}
          </div>
          {isShowState ? (
            <div className="relative mx-auto p-4 mt-4 box-shadow rounded bg-white">
              <CloseIconButton onClick={() => setShow(false)} />
              <AgmtDetails agmtDetails={agmtDetails} />
            </div>
          ) : isEditState ? (
            <div className=" bg-white h-full p-1 text-xs mx-3">
              <div className="box-shadow rounded-lg mt-3 pb-2 mb-3">
                <AgreementForm getAgmtRefetch={agmtRefetch} />
              </div>
            </div>
          ) : (
            <>
              <div className="flex my-2">
                {filters.map((filter, index) => (
                  <button
                    key={index}
                    onClick={() => updatedDataHandler(filter, index)}
                    className={`${
                      activeTab === index &&
                      "border-b-4 border-primary border-primary font-bold bg-gray-100"
                    } 
                    ${
                      selectedRows?.length > 0 &&
                      "opacity-40 cursor-not-allowed"
                    }  w-1/12 text-sm text-tertiary py-2 px-4`}
                    disabled={selectedRows?.length > 0}
                  >
                    {filter}
                  </button>
                ))}
              </div>

              {massUploadData?.length > 0 && !isEditMassUpload && (
                <div className="text-secondary italic text-xs3 mx-2">
                  <p className="font-bold italic inline-block mr-1">Note: </p>
                  Tick the box to delete some data.
                </div>
              )}

              <div className="mt-3">
                <MassUploadTable
                  data={massUploadData}
                  columns={columns}
                  selectedRows={selectedRows}
                  onSelectedRowsChange={handleSelectedRowsChange}
                  setOpenModal={setOpenModal}
                  clearSelectedRows={toggleClear}
                  isLoading={isLoading}
                  isEdit={isEditMassUpload}
                  agmtRefetch={agmtRefetch}
                  setActiveTab={setActiveTab}
                />
              </div>
            </>
          )}
        </>
        <ConfirmBoxDelete
          ModalTitle="Delete Agreement"
          qConfirmation={"Are you sure you want to delete agreement(s)?"}
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          onClickFunction={handleDelete}
        />
      </>
    </Modal>
  );
};
